<script setup lang="ts">
import unexpectedEventsAndLongTimeContinuousDrivingExtensions from "src/types/responses/unexpectedEventsAndLongTimeContinuousDrivingExtensions";
import ResizableList from '@/components/ResizableList.vue';
import EditOrDeleteButtons from "@/components/UnexpectedEventAndAlertStatuseList/EditOrDeleteButtons.vue"
import { formatDateTime, formatDate } from "@/services/functions";
import getAccountResponse from "@/types/responses/getAccount";
import CopyElement from "@/components/CopyElement.vue";
import { computed } from 'vue';
import i18n from '@/lang/i18n';

const props = defineProps<{
  unexpectedEventAndAlertStatuse: unexpectedEventsAndLongTimeContinuousDrivingExtensions[]
  searched: boolean
  isOperationOffice: boolean
  isDepartment: boolean
  accounts: getAccountResponse[]
  isLoading: boolean
  deleteAuthority: boolean
  openUnexpectedModal: (item: unexpectedEventsAndLongTimeContinuousDrivingExtensions) => void
  openDeleteConfirmModal: (item: unexpectedEventsAndLongTimeContinuousDrivingExtensions) => void
}>()

const HEADER = ([
  i18n.global.t('pages.unexpectedEventAndAlertStatuses.listHeader.tripId'),
  i18n.global.t('pages.unexpectedEventAndAlertStatuses.listHeader.division'),
  i18n.global.t('pages.unexpectedEventAndAlertStatuses.listHeader.startTime'),
  i18n.global.t('pages.unexpectedEventAndAlertStatuses.listHeader.endTime'),
  i18n.global.t('pages.unexpectedEventAndAlertStatuses.listHeader.numberPlate'),
  i18n.global.t('pages.unexpectedEventAndAlertStatuses.listHeader.driverName'),
  i18n.global.t('pages.unexpectedEventAndAlertStatuses.listHeader.edit'),
  i18n.global.t('pages.unexpectedEventAndAlertStatuses.listHeader.updatedByAndDate')
])

const makeDivision = computed(() => (division: string) => {
  if (division == 'unexpected_events') return i18n.global.t('pages.unexpectedEventAndAlertStatuses.unexpectedEvents')
  if (division == 'long_time_continuous_driving_extensions') return i18n.global.t('pages.unexpectedEventAndAlertStatuses.longTimeContinuousDriving')
  return ''
})

const updatedBy = computed(() => (item: unexpectedEventsAndLongTimeContinuousDrivingExtensions) => {
  return props.accounts.find((account: getAccountResponse) => {
    return account.id == item.updated_by
  })
})
</script>
<template>
  <div class="unexpected-event-and-alert-statuse-list">
    <template v-if="!props.searched && !props.isOperationOffice && !props.isDepartment">
      <div class="not-searched">
        <label>
          {{ i18n.global.t('pages.common.list.runFilter') }}
        </label>
      </div>
    </template>
    <template v-if="props.isLoading">
      <div class="not-searched">
        <label>
          {{ i18n.global.t('pages.common.list.reading') }}
        </label>
      </div>
    </template>
    <ResizableList
      scrollAreaSelector=".v-table__wrapper"
    >
      <div class="table-scrollbar">
        <v-table>
          <thead>
            <tr class="table-header-row">
              <template v-for="(item, index) in HEADER" :key="item">
                <th>
                  <div :class="index === 0 ? 'ml-2' : ''">
                    {{ item }}
                  </div>
                </th>
              </template>
            </tr>
          </thead>
          <tbody>
            <template v-if="props.unexpectedEventAndAlertStatuse?.length != 0">
              <tr
                v-for="(item, index) in props.unexpectedEventAndAlertStatuse"
                :key="index"
              >
                <td class="pl-0">
                  <div class="d-flex flex-row">
                    <div class="d-flex flex-column justify-content-center">
                      <div class="d-flex ml-2">
                        {{ item.delivery_plan_id.substr(-7, 7) }}
                      </div>
                    </div>
                    <div class="d-flex flex-column justify-content-center">
                      <CopyElement
                        :copyText="item.delivery_plan_id"
                        size="small"
                      />
                    </div>
                  </div>
                </td>
                <td>{{ makeDivision(item.division_name) }}</td>
                <td>{{ formatDateTime(new Date(item.start_time)) }}</td>
                <td>{{ formatDateTime(new Date(item.end_time)) }}</td>
                <td>{{ item.car_number }}</td>
                <td>{{ item.driver_name }}</td>
                <td>
                  <EditOrDeleteButtons
                    :item="item"
                    :deleteAuthority="props.deleteAuthority"
                    :openUnexpectedModal="props.openUnexpectedModal"
                    :openDeleteConfirmModal="props.openDeleteConfirmModal"
                  />
                </td>
                <td class="column-space">
                  {{ updatedBy(item)?.name }}/{{ formatDate(new Date(item.updated_at)) }}
                </td>
              </tr>
            </template>
            <template v-else-if="props.searched && !props.isLoading">
              <tr>
                <td colspan="9">
                  {{ i18n.global.t('pages.common.list.noData') }}
                </td>
              </tr>
            </template>
          </tbody>
        </v-table>
      </div>
    </ResizableList>
  </div>
</template>
<style>
.unexpected-event-and-alert-statuse-list .v-table__wrapper {
  height: var(--resizable-height, var(--list-default-height));
  overflow: auto;
}
</style>
<style scoped>
.unexpected-event-and-alert-statuse-list {
  position: relative;
}
.unexpected-event-and-alert-statuse-list table th {
  background-color: #f8f9fa;
  position: sticky;
  top: 0;
  min-width: 7em;
}
.checkbox-in-grid {
  padding: 1vm;
  height: inherit !important;
  display: inline-grid !important;
}
.list-loading {
  position: absolute;
  min-width: 58vw;
  height: 100%;
  overflow: auto;
  z-index: 1000;
  background-color: rgba(80,80,80,0.5);
  right: 0;
  left: 0;
}
.list-loading>label {
  font-weight: bold;
  color: white;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateY(-50%) translateX(-50%);
}
.column-space {
  white-space: nowrap;
}
</style>
